/* BHS slideshow autoplay driver.

   Horizon's core slideshow.js schedules autoplay with a single fixed
   `setInterval` at the section Speed and never re-reads it. For morph slides we
   need a per-slide interval (the morph must be centred in the slide's time), so
   this script owns autoplay scheduling for autoplaying slideshows:

   - Normal slide  : advances after the section Speed.
   - Morph slide   : advances after `hold + morph + hold` seconds, and the morph
                     image block delays its crossfade by `hold` seconds so the
                     transition is centred (starts at hold, finishes at hold+morph,
                     second image shows for hold more).

   It also removes Horizon's hover-pause listeners so the slider keeps moving
   while the pointer is over it. Pause/play buttons, arrows, dots, drag and
   `prefers-reduced-motion` are preserved: we never advance a paused slideshow.

   Plain JS, no dependencies. */
(function () {
  'use strict';

  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  if (motionQuery && motionQuery.matches) return;

  var states = new WeakMap();
  var protoSuspend = null;
  var protoResume = null;

  function baseInterval(slider) {
    var v = parseInt(slider.getAttribute('autoplay'), 10);
    return Number.isNaN(v) ? 5000 : v * 1000;
  }

  function currentSlide(slider) {
    var slides = (slider.refs && slider.refs.slides) || slider.slides;
    if (!slides || !slides.length) return null;
    /* Trust the component's index — in crossfade mode every stacked slide reports
       aria-hidden="false", so searching by that attribute would always find slide 0. */
    if (typeof slider.current === 'number' && slides[slider.current]) return slides[slider.current];
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('aria-hidden') === 'false') return slides[i];
    }
    return slides[0];
  }

  /* Period in ms for the current slide. Morph = hold + morph + hold. */
  function periodMs(slider) {
    var slide = currentSlide(slider);
    var holder = slide && slide.querySelector ? slide.querySelector('[data-bhs-media-mode="dual-morph"]') : null;
    if (!holder) return baseInterval(slider);

    var secs = parseFloat(holder.dataset.bhsMorphSecs) || 2.5;
    var hold = parseFloat(holder.dataset.bhsMorphHold) || 3;
    return (hold + secs + hold) * 1000;
  }

  function morphHoldMs(slider) {
    var slide = currentSlide(slider);
    var holder = slide && slide.querySelector ? slide.querySelector('[data-bhs-media-mode="dual-morph"]') : null;
    if (!holder) return null;
    var hold = parseFloat(holder.dataset.bhsMorphHold);
    return Number.isNaN(hold) ? 3000 : hold * 1000;
  }

  function allSlides(slider) {
    return (slider.refs && slider.refs.slides) || slider.slides || [];
  }

  function clearRunClass(slider) {
    allSlides(slider).forEach(function (s) {
      s.classList.remove('bhs-morph-run');
    });
  }

  function clear(state) {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = 0;
    }
    if (state.runTimer) {
      clearTimeout(state.runTimer);
      state.runTimer = 0;
    }
  }

  function schedule(slider, state) {
    clear(state);
    clearRunClass(slider);
    if (!slider.isConnected) return;
    if (slider.paused) return;

    /* Morph slides: hold the first image for the hold time, then start the crossfade.
       Runs on both autoplay and manual navigation; only paused freezes it. */
    var hold = morphHoldMs(slider);
    if (hold != null) {
      state.runTimer = setTimeout(function () {
        state.runTimer = 0;
        if (!slider.isConnected || slider.paused) return;
        var slide = currentSlide(slider);
        if (slide) slide.classList.add('bhs-morph-run');
      }, hold);
    }

    if (!slider.autoplay) return;

    state.timer = setTimeout(function () {
      state.timer = 0;
      if (!slider.isConnected) return;
      if (slider.paused || !slider.autoplay) return;
      if (document.hidden) {
        schedule(slider, state);
        return;
      }
      if (typeof slider.next === 'function') slider.next();
      /* next() dispatches slideshow:select, which reschedules. */
    }, periodMs(slider));
  }

  /* Motion (Ken Burns, parallax drift, morph, text reveal) is CSS keyed on the
     active slide. Horizon marks the first slide active at page load, so without
     this a slider below the fold plays every animation before the visitor reaches
     it. Add .bhs-in-view to the section only once it enters the viewport (or
     immediately when already on screen, e.g. the hero), which wakes the CSS. */
  function gateInView(section) {
    if (section.dataset.bhsInViewGuard === 'true') return;
    section.dataset.bhsInViewGuard = 'true';
    /* Mark that JS is present so the CSS hold-back only applies with JS. */
    section.classList.add('bhs-motion-ready');

    function show() {
      section.classList.add('bhs-in-view');
    }

    if (!('IntersectionObserver' in window)) {
      show();
      return;
    }

    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        show();
        observer.disconnect();
      });
    }, { threshold: 0.15 });
    io.observe(section);
  }

  function install(slider) {
    if (states.has(slider)) return;
    var proto = Object.getPrototypeOf(slider);
    if (protoSuspend === null && typeof proto.suspend === 'function') {
      protoSuspend = proto.suspend;
      protoResume = proto.resume;
    }

    var state = { timer: 0 };
    states.set(slider, state);

    /* Stop Horizon's fixed interval and take over scheduling. */
    if (typeof protoSuspend === 'function') protoSuspend.call(slider);

    /* Remove the hover-pause listeners core added (keep manual nav + pause btn). */
    if (typeof protoSuspend === 'function') slider.removeEventListener('mouseenter', protoSuspend);
    if (typeof protoResume === 'function') slider.removeEventListener('mouseleave', protoResume);

    slider.suspend = function () {
      if (typeof protoSuspend === 'function') protoSuspend.call(slider);
      clear(state);
    };

    slider.play = function () {
      slider.paused = false;
      schedule(slider, state);
    };

    slider.addEventListener('slideshow:select', function () {
      schedule(slider, state);
    });

    schedule(slider, state);
  }

  /* ---- Slide transition (crossfade / continuous camera) ------------------

     crossfade: adds .bhs-slides--crossfade to the slides container, so every slide
     stacks in one grid cell and the active one fades in over the outgoing one. The
     native select, drag, autoplay and reduced-motion keep working; the CSS fades.

     continuous camera: the same fade, but the incoming slide's Ken Burns is restarted
     at the cut so the movement runs through the transition instead of sitting still. */

  function slidesContainer(slider) {
    return slider.querySelector('slideshow-slides') ||
      (slider.refs && slider.refs.scroller) ||
      null;
  }

  function slidesList(slider) {
    return slider.slides ||
      (slider.refs && slider.refs.slides) ||
      slider.querySelectorAll('slideshow-slide');
  }

  /* The native component reports several slides as aria-hidden="false" once they
     share a grid cell, so the active slide is tracked with our own class instead. */
  function setActive(slider, index) {
    var slides = slidesList(slider);
    var i;
    for (i = 0; i < slides.length; i++) {
      slides[i].classList.toggle('bhs-slide--active', i === index);
    }
  }

  /* Stacking every slide means the native geometry check counts them all as visible,
     which makes nextIndex jump by the slide count. In crossfade only one slide is ever
     shown, so pin visibleSlides to the current one — next/previous then step by one. */
  function forceSingleVisible(slider) {
    if (slider.__bhsSingleVisible) return;
    slider.__bhsSingleVisible = true;
    Object.defineProperty(slider, 'visibleSlides', {
      configurable: true,
      get: function () {
        var slides = slidesList(this);
        return [slides[this.current] || slides[0]];
      }
    });
  }

  /* Ken Burns across the fade.
     A slide's Ken Burns duration equals its on-screen time (both come from the slide
     Speed), so its pan finishes exactly as that slide ends. The incoming slide must
     therefore start its own pan from zero; otherwise it is left parked at the end of
     its run (fully panned) and reads as "no motion". We restart the animation the
     reliable way — clear the inline animation, force a reflow, then let the class rule
     re-create it from the start. This is deterministic and immune to the race where a
     slide's animation finishes on the very frame it is shown (which a timeline seek
     loses). Works on every .bhs-kb element (dual slides have several). */
  function cameraEls(slide) {
    return slide ? Array.prototype.slice.call(slide.querySelectorAll('.bhs-kb')) : [];
  }

  function restartCamera(slide) {
    cameraEls(slide).forEach(function (el) {
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
    });
  }

  function setupTransition(section) {
    if (section.dataset.bhsTransitionReady === 'true') return;
    var style = section.getAttribute('data-bhs-transition');
    if (!style || style === 'none') return;
    section.dataset.bhsTransitionReady = 'true';

    section.querySelectorAll('slideshow-component').forEach(function (slider) {
      var box = slidesContainer(slider);
      if (box) box.classList.add('bhs-slides--crossfade');
      if (style === 'camera') section.classList.add('bhs-transition--camera');
      forceSingleVisible(slider);
      var slides = slidesList(slider);
      var start = typeof slider.current === 'number' ? slider.current : (Number(slider.getAttribute('initial-slide')) || 0);
      setActive(slider, start);
      slider.__bhsOutgoing = slides[start] || null;
      slider.addEventListener('slideshow:select', function () {
        var list = slidesList(slider);
        var next = slider.current;
        slider.__bhsOutgoing = list[next] || slider.__bhsOutgoing;
        requestAnimationFrame(function () {
          setActive(slider, next);
          if (list[next]) restartCamera(list[next]);
        });
      });
    });
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.bhs-slider slideshow-component').forEach(function (slider) {
      var section = slider.closest('.bhs-slider');
      if (section) gateInView(section);
      install(slider);
    });
    root.querySelectorAll('.bhs-slider').forEach(setupTransition);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
